import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isTradeOwner, resolveComplianceTrades } from '@trade-connector/shared/lib/complianceTrades';
import { DocumentRequirement } from './useDocuments';

interface PersonalDocument {
    id: number;
    document_type: string;
    verification_status: string;
    file_name: string;
    created_at: string | null;
    expiry_date?: string | null;
}

export function usePersonalDocumentCompliance() {
    const { user } = useAuth();
    const [requirements, setRequirements] = useState<DocumentRequirement[]>([]);
    const [documents, setDocuments] = useState<PersonalDocument[]>([]);
    const [amrGroupByDocType, setAmrGroupByDocType] = useState<Map<string, string>>(new Map());
    const [loading, setLoading] = useState(true);

    const loadPersonalCompliance = useCallback(async () => {
        if (!user?.id || !user?.trade_company_id) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);

            // 1. Get user's personal specializations + company data
            const { data: profileData, error: profileError } = await supabase
                .from('profiles')
                .select('specializations, is_field_worker, operating_states, trade_companies(specializations, states_of_operation, business_state)')
                .eq('id', user.id)
                .maybeSingle();

            if (profileError) throw profileError;

            // Non-field-workers don't need personal compliance documents
            if (!profileData?.is_field_worker) {
                setRequirements([]);
                setDocuments([]);
                setLoading(false);
                return;
            }

            type ProfileWithCompany = {
                specializations: string[] | null;
                is_field_worker: boolean;
                operating_states: string[] | null;
                trade_companies: { specializations: string[] | null; states_of_operation: string[] | null; business_state: string | null } | null;
            };
            const profile = profileData as ProfileWithCompany;

            const specializations = resolveComplianceTrades(
                profile.specializations,
                profile.trade_companies?.specializations,
                isTradeOwner(user?.roles),
            ).trades;

            // Multi-state: prefer individual operating_states, fall back to company's states_of_operation
            const companyStates = profile.trade_companies?.states_of_operation
                ?? (profile.trade_companies?.business_state ? [profile.trade_companies.business_state] : []);
            const operatingStates = profile.operating_states ?? companyStates;

            // 2. Fetch INDIVIDUAL-scoped requirements based on user's specializations and global individual requirements
            let query = supabase
                .from('document_requirements')
                .select('*')
                .eq('company_type', 'TRADE')
                .eq('is_mandatory', true)
                .eq('requirement_scope', 'INDIVIDUAL');

            if (specializations.length > 0) {
                query = query.or(`trade_type.in.(${specializations.join(',')}),trade_type.is.null`);
            } else {
                query = query.is('trade_type', null);
            }

            const { data: reqData, error: reqError } = await query;
            if (reqError) throw reqError;

            // Filter by state
            const stateFiltered = (reqData || []).filter((req: Record<string, unknown>) => {
                if (!req.state) return true;
                if (!operatingStates.length) return false;
                return operatingStates.includes(req.state as string);
            });

            // Deduplicate by document_type
            const seenDocTypes = new Set<string>();
            const uniqueReqs = stateFiltered.filter((req: Record<string, unknown>) => {
                if (seenDocTypes.has(req.document_type as string)) return false;
                seenDocTypes.add(req.document_type as string);
                return true;
            });

            setRequirements(uniqueReqs as unknown as DocumentRequirement[]);

            // 3. Fetch accreditation-based individual requirements for this user
            const { data: userAccreds } = await supabase
                .from('user_accreditations')
                .select('accreditation_id')
                .eq('user_id', user.id);

            if (userAccreds?.length) {
                const accredIds = userAccreds.map(a => a.accreditation_id);
                const { data: accredReqs } = await supabase
                    .from('document_requirements')
                    .select('*')
                    .eq('company_type', 'TRADE')
                    .eq('requirement_scope', 'INDIVIDUAL')
                    .eq('is_mandatory', true)
                    .in('accreditation_id', accredIds);

                if (accredReqs?.length) {
                    const accredFiltered = (accredReqs as unknown as DocumentRequirement[]).filter(req => {
                        if (!req.state) return true;
                        if (!operatingStates.length) return false;
                        return operatingStates.includes(req.state as string);
                    }).filter(req => {
                        // Dedup against already-seen types
                        if (seenDocTypes.has(req.document_type)) return false;
                        seenDocTypes.add(req.document_type);
                        return true;
                    });
                    if (accredFiltered.length) {
                        setRequirements(prev => [...prev, ...accredFiltered]);
                    }
                }
            }

            // 4. Fetch personal documents and AMR groups concurrently
            const docsPromise = supabase
                .from('trade_documents')
                .select('id, document_type, verification_status, file_name, created_at, expiry_date')
                .eq('profile_id', user.id)
                .eq('owner_type', 'PROFILE')
                .eq('is_draft', false)
                .eq('is_current', true)
                .order('upload_timestamp', { ascending: false });

            const docTypesPromise = supabase
                .from('document_types')
                .select('code, amr_group')
                .not('amr_group', 'is', null);

            const [docsResponse, docTypesResponse] = await Promise.all([
                docsPromise,
                docTypesPromise
            ]);

            if (docsResponse.error) throw docsResponse.error;
            if (docTypesResponse.error) throw docTypesResponse.error;
            const docsData = docsResponse.data;
            setDocuments((docsData || []) as PersonalDocument[]);

            const docTypes = docTypesResponse.data;
            const amrMap = new Map<string, string>();
            if (docTypes) {
                docTypes.forEach(d => {
                    if (d.amr_group) amrMap.set(d.code, d.amr_group);
                });
            }
            setAmrGroupByDocType(amrMap);

        } catch (err) {
            console.error('Error loading personal compliance:', err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadPersonalCompliance();
    }, [loadPersonalCompliance]);

    // Calculate aggregated metrics exactly like useDocumentCompliance

    // Deduplicate mandatory requirements by document_type (already done but safe to re-check if needed, though they are stored deduped)
    const requiredTypes = new Set(requirements.map(r => r.document_type));
    
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);

    const verifiedTypes = new Set<string>();
    const pendingTypes = new Set<string>();
    const rejectedTypes = new Set<string>();
    const expiredTypes = new Set<string>();
    const expiringSoonTypes = new Set<string>();
    const missingTypes = new Set<string>(requiredTypes);

    // Map requirements by their AMR group if they have one
    const amrReqGroups = new Map<string, string[]>();
    for (const reqType of requiredTypes) {
        const group = amrGroupByDocType.get(reqType);
        if (group) {
            if (!amrReqGroups.has(group)) amrReqGroups.set(group, []);
            amrReqGroups.get(group)!.push(reqType);
        }
    }

    documents.forEach(doc => {
        let matchingReqType = doc.document_type;
        // If exact type is not required, check if AMR group matches a required type
        if (!requiredTypes.has(matchingReqType)) {
            const group = amrGroupByDocType.get(doc.document_type);
            if (group && amrReqGroups.has(group)) {
                // Use the first required type in this group
                matchingReqType = amrReqGroups.get(group)![0];
            } else {
                return; // Document doesn't match any requirement
            }
        }

        const expiryDate = doc.expiry_date;
        const isExpired = expiryDate && new Date(expiryDate) <= now;
        
        if (isExpired) {
            // Document is expired, so it doesn't fulfill the requirement
            expiredTypes.add(matchingReqType);
        } else {
            // It's not expired, so it removes the "missing" requirement
            if (doc.verification_status !== 'REJECTED') {
                missingTypes.delete(matchingReqType);
            }

            if (doc.verification_status === 'APPROVED' || doc.verification_status === 'VERIFIED') {
                verifiedTypes.add(matchingReqType);
                if (expiryDate) {
                    const expDate = new Date(expiryDate);
                    if (expDate <= thirtyDaysFromNow) {
                        expiringSoonTypes.add(matchingReqType);
                    }
                }
            } else if (doc.verification_status === 'PENDING') {
                pendingTypes.add(matchingReqType);
            } else if (doc.verification_status === 'REJECTED') {
                rejectedTypes.add(matchingReqType);
            }
        }
    });

    const totalRequired = requirements.length;
    
    return {
        requirements,
        documents,
        totalRequired,
        uploaded: documents.length,
        verified: verifiedTypes.size,
        missing: Array.from(missingTypes),
        expired: Array.from(expiredTypes),
        expiringSoon: Array.from(expiringSoonTypes),
        pending: Array.from(pendingTypes),
        completionPercentage: totalRequired > 0 ? Math.round((verifiedTypes.size / totalRequired) * 100) : 0,
        loading,
        refetch: loadPersonalCompliance
    };
}
